import { isShiftIgnoredIn, placeShifts, type IgnoredTerm, type PlaceShift } from '@dispositio/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { PluginKey } from '@tiptap/pm/state';
import { suggestExtension, type SuggestAct, type SuggestLive, type SuggestState } from './suggest';

/**
 * Mögliche Ortswechsel im Draft einer Szene (PLAN.md §16a): Wo der verknüpfte Ort im Text ein anderer wird als der davor (außerhalb von
 * Rede und Gedanken), ist der neue Ort als Vorschlag markiert (`suggest.ts`); ✓ macht ihn zum Schauplatz der Szene, ✕ merkt „kein
 * Ortswechsel“ für diesen Wechsel im Dokument. Ein Wechsel zum Schauplatz selbst ist kein Hinweis wert.
 */

export type ShiftAct = SuggestAct;

export interface PlaceLinkAt {
  entityId: string;
  key: string;
  from: number;
  to: number;
}

export type ShiftHit = PlaceShift<PlaceLinkAt> & { fromKey: string };

export interface ShiftLive extends SuggestLive<ShiftHit> {
  /** Eintrag zu einem Verweisschlüssel, wenn er ein Ort ist */
  placeOf: (key: string) => string | null;
  /** Schauplatz der Szene */
  setting: string | null;
  ignored: readonly IgnoredTerm[] | undefined;
  title: (hit: ShiftHit) => string;
}

export const placeShiftKey = new PluginKey<SuggestState<ShiftHit>>('placeShift');

const ELSEWHERE = new Set(['rede', 'gedanke']);

/** Verknüpfte Orte im Dokument in Textreihenfolge (ein Verweis über mehrere Textstücke zählt einmal), ohne die in Rede und Gedanken */
export function placeLinksAt(doc: PMNode, placeOf: (key: string) => string | null): PlaceLinkAt[] {
  const out: PlaceLinkAt[] = [];
  doc.descendants((node, pos) => {
    if (!node.isText) return true;
    const link = node.marks.find((m) => m.type.name === 'refLink');
    const key = link?.attrs.key as string | undefined;
    if (!key) return false;
    if (node.marks.some((m) => m.type.name === 'refSpan' && ELSEWHERE.has(m.attrs.role as string))) return false;
    const last = out[out.length - 1];
    if (last && last.key === key && last.to === pos) {
      last.to = pos + node.nodeSize;
      return false;
    }
    const entityId = placeOf(key);
    if (entityId) out.push({ entityId, key, from: pos, to: pos + node.nodeSize });
    return false;
  });
  return out;
}

/** Mögliche Ortswechsel im Dokument: ohne den zum Schauplatz und ohne ignorierte */
function shiftHits(doc: PMNode, l: ShiftLive): ShiftHit[] {
  const links = placeLinksAt(doc, l.placeOf);
  const keyOf = new Map(links.map((x) => [x.entityId, x.key]));
  return placeShifts(links)
    .map((s) => ({ ...s, fromKey: keyOf.get(s.fromId)! }))
    .filter((s) => s.entityId !== l.setting && !isShiftIgnoredIn(l.ignored, s.fromKey, s.key));
}

export const placeShift = (live: { current: ShiftLive }) =>
  suggestExtension<ShiftHit, ShiftLive>(
    {
      name: 'placeShift',
      kind: 'place',
      key: placeShiftKey,
      hits: shiftHits,
      mark: (h, l) => ({ class: 'rt-place-shift', 'data-place-shift': h.key, title: l.title(h) }),
      word: (h) => h.key,
      testId: 'place',
    },
    live,
  );
